// Shared presentation of a resource's public market profile: the target's unit
// and resolution, its submission horizons, and how fresh its measurements are.
// Used by the public markets directory (MarketDetails) and the signed-in
// resources page (ResourceDetails) so the two cannot drift apart.

// Physical unit symbols. Mirrors the backend `MeasurementUnits` choices
// (api/users/models/user_resources.py); the API sends the raw code.
const UNIT_SYMBOL = {
  w: 'W',
  kw: 'kW',
  mw: 'MW',
  mwh: 'MWh',
  eur_mwh: 'EUR/MWh',
};

export const unitSymbol = (code) => UNIT_SYMBOL[code] || (code || '').toUpperCase();

// Publisher name shortened to a single word, for credits with no room for the
// full one (the chart legend): "Elia Open Data" -> "Elia", "ENTSO-E
// Transparency Platform" -> "ENTSO-E", "ESIOS · Red Eléctrica de España" ->
// "ESIOS". The full name still has to reach the reader, so call sites keep it
// in the link's `title`.
export const shortSourceName = (name) => {
  const first = String(name || '').split('·')[0].trim();
  if (!first) return '';
  const word = first.split(/\s+/)[0];
  // A three-letter leading token ("Red Eléctrica de España") identifies
  // nothing on its own; keep the whole segment rather than shorten to noise.
  return word.length >= 4 ? word : first;
};

/**
 * Publisher of a resource's ground-truth measurements, normalised across the
 * two payload shapes: the authenticated resource list returns the model fields
 * flat, the anonymous one nests them under `profile`.
 * @returns {{name: string, shortName: string, url: string}} empty strings when
 *   the market owner has not recorded a source
 */
export const resourceSource = (resource) => {
  const name = resource?.profile?.source_name || resource?.source_name || '';
  const url = resource?.profile?.source_url || resource?.source_url || '';
  return {name, url, shortName: shortSourceName(name)};
};

// Sampling resolution as a chip label: 15 -> "15 min", 60 -> "1 h".
export const resolutionLabel = (minutes) => {
  const m = parseInt(minutes, 10);
  if (!Number.isFinite(m) || m <= 0) return null;
  if (m % 60 === 0) return `${m / 60} h`;
  return `${m} min`;
};

// A horizon as a submission commitment rather than a schedule spec: what the
// forecast covers, and how often a gate closes. `cadence` arrives from
// `program_summary` as "hourly" or "daily 10:00 CET".
export const horizonCommitment = (h) => {
  const cadence = h.cadence || '';
  if (h.challenge_type === 'intraday') {
    return {
      label: 'Intraday',
      detail: `covers the 24 h after each gate closure · ${cadence || 'hourly'}`,
    };
  }
  if (h.horizon_offset === 1) {
    return {
      label: 'Day-ahead',
      detail: `the next calendar day, 00:00–24:00 local · ${cadence || 'daily'}`,
    };
  }
  return {
    label: `D+${h.horizon_offset}`,
    detail: `one target day, ${h.horizon_offset} days ahead · ${cadence || 'daily'}`,
  };
};

// How far behind the target series is, expressed in its own sampling intervals
// so the threshold scales with the resource instead of being a fixed number of
// days. A day's worth of missing intervals is normal for a settled feed that
// re-imports overnight; a week is not.
export const LAGGING_INTERVALS = 96;   // ~1 day at 15 min
export const STALE_INTERVALS = 480;    // ~5 days at 15 min

/**
 * Freshness of a resource's measurements.
 * @param {string|Date} latest last measured timestamp (`end_datetime`)
 * @param {number} resolutionMinutes sampling resolution
 * @param {Date} [now] injectable clock, for tests
 * @returns {{state: 'unknown'|'fresh'|'lagging'|'stale', intervals: number|null}}
 */
export const freshness = (latest, resolutionMinutes, now = new Date()) => {
  const res = parseInt(resolutionMinutes, 10);
  const at = latest ? new Date(latest) : null;
  if (!at || Number.isNaN(at.getTime()) || !Number.isFinite(res) || res <= 0) {
    return {state: 'unknown', intervals: null};
  }
  const intervals = Math.max(0, Math.floor((now - at) / (res * 60 * 1000)));
  if (intervals >= STALE_INTERVALS) return {state: 'stale', intervals};
  if (intervals >= LAGGING_INTERVALS) return {state: 'lagging', intervals};
  return {state: 'fresh', intervals};
};

/**
 * Share of the published span that actually has a measurement, as a percentage.
 * Returns null when the span or the sample count is unusable, so callers can
 * hide the bar rather than draw a misleading 0%.
 */
export const coveragePercent = (metadata, resolutionMinutes) => {
  const res = parseInt(resolutionMinutes, 10);
  const samples = parseInt(metadata?.n_samples, 10);
  const start = metadata?.start_datetime ? new Date(metadata.start_datetime) : null;
  const end = metadata?.end_datetime ? new Date(metadata.end_datetime) : null;
  if (!Number.isFinite(res) || res <= 0) return null;
  if (!Number.isFinite(samples) || samples <= 0) return null;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const expected = Math.floor((end - start) / (res * 60 * 1000)) + 1;
  if (expected <= 0) return null;
  return Math.min(100, Math.round((samples / expected) * 1000) / 10);
};

// "6 y 7 mo" for the rail's history tile. Deliberately terse: it sits under a
// large sample count, not on its own.
export const spanLabel = (metadata) => {
  const start = metadata?.start_datetime ? new Date(metadata.start_datetime) : null;
  const end = metadata?.end_datetime ? new Date(metadata.end_datetime) : null;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const months = Math.max(
    0,
    (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())
  );
  const years = Math.floor(months / 12);
  const rest = months % 12;
  if (years === 0) return `${rest} mo`;
  if (rest === 0) return `${years} y`;
  return `${years} y ${rest} mo`;
};
